import AssignmentIcon from '@mui/icons-material/Assignment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, LinearProgress, Paper, Typography } from '@mui/material';

import type { User } from '../types';

interface AssignmentsQueueHeaderProps {
  remainingCount: number;
  totalCount: number;
  currentUser: User;
  onOpenQueue: () => void;
  onNavigateToAdmin: () => void;
}

/**
 * Top header for the Labelling Interface: the labeller's queue summary and
 * "View Queue" trigger on the left, user identity and admin link on the right.
 */
export function AssignmentsQueueHeader({
  remainingCount,
  totalCount,
  currentUser,
  onOpenQueue,
  onNavigateToAdmin,
}: AssignmentsQueueHeaderProps) {
  const doneCount = totalCount - remainingCount;
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <Box
      sx={{
        mb: 2.5,
        pb: 1.5,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Left: queue summary card */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          pr: 1.5,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          bgcolor: 'rgba(25, 118, 210, 0.04)',
          borderColor: 'rgba(25, 118, 210, 0.25)',
          flex: '1 1 auto',
          maxWidth: 720,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 240 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'white',
              flexShrink: 0,
            }}
          >
            <AssignmentIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Your Labelling Queue
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalCount === 0 ? (
                'No assignments yet'
              ) : (
                <>
                  {doneCount} of {totalCount} completed ({pct}%) ·{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {remainingCount} remaining
                  </Box>
                </>
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 220 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={pct === 100 && totalCount > 0 ? 'success' : 'primary'}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onOpenQueue}
            endIcon={<ChevronRightIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              py: 0.5,
              px: 1.5,
            }}
          >
            View Queue
          </Button>
        </Box>
      </Paper>

      {/* Right: user identity & admin link */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          Logged in as:{' '}
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {currentUser.firstName} {currentUser.lastName}
          </Box>
        </Typography>

        {currentUser.userType === 'admin' && (
          <Button
            variant="outlined"
            color="primary"
            onClick={onNavigateToAdmin}
            size="small"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
          >
            Admin Interface
          </Button>
        )}
      </Box>
    </Box>
  );
}
